import { useMemo } from 'react';
import { useLoansStore } from './useSupabaseStore';
import { useLocalStorage } from './useLocalStorage';

export interface AppNotification {
  id: string;
  loanId: string;
  customerName: string;
  assetName: string;
  loanAmount: number;
  dueDate: string;
  daysLeft: number;
  type: 'overdue' | 'due_soon';
  createdAt: string;
}

export function useNotifications() {
  const { loans } = useLoansStore();
  const [readIds, setReadIds] = useLocalStorage<string[]>('notifications_read_v2', []);

  const notifications: AppNotification[] = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return loans
      .filter((l) => l.status !== 'redeemed' && l.status !== 'forfeited')
      .map((l) => {
        const due = new Date(l.dueDate);
        due.setHours(0, 0, 0, 0);
        const diff = Math.ceil(
          (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
        );
        return { ...l, daysLeft: diff };
      })
      .filter((l) => l.daysLeft <= 7)
      .sort((a, b) => a.daysLeft - b.daysLeft)
      .map((l) => ({
        id: l.daysLeft < 0 ? `loan_overdue_${l.id}` : `loan_due_soon_${l.id}`,
        loanId: l.id,
        customerName: l.customerName,
        assetName: l.assetName,
        loanAmount: l.loanAmount,
        dueDate: l.dueDate,
        daysLeft: l.daysLeft,
        type: l.daysLeft < 0 ? ('overdue' as const) : ('due_soon' as const),
        createdAt: new Date().toISOString(),
      }));
  }, [loans]);

  const unreadNotifications = useMemo(
    () => notifications.filter((n) => !readIds.includes(n.id)),
    [notifications, readIds]
  );

  const readNotifications = useMemo(
    () => notifications.filter((n) => readIds.includes(n.id)),
    [notifications, readIds]
  );

  const unreadCount = unreadNotifications.length;

  const markAsRead = (id: string) => {
    setReadIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
  };

  const markAllAsRead = () => {
    const allIds = notifications.map((n) => n.id);
    setReadIds((prev) => [...new Set([...prev, ...allIds])]);
  };

  const markAsUnread = (id: string) => {
    setReadIds((prev) => prev.filter((x) => x !== id));
  };

  return {
    notifications,
    unreadNotifications,
    readNotifications,
    readIds,
    unreadCount,
    markAsRead,
    markAllAsRead,
    markAsUnread,
  };
}